/**
 * TimeShiftDialog
 * Dialog for confirming time shift to before/after a deadline.
 * Pattern: Molecule - composes atoms for user interaction.
 */
import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { format, addHours, subHours } from 'date-fns';

export interface TimeShiftDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (targetTime: Date) => void;
  deadline: Date;
  mode: 'before' | 'after';
  deadlineLabel: string;
  isPending: boolean;
  isSettingTime?: boolean;
  isTransitioning?: boolean;
}

export function TimeShiftDialog({
  open,
  onClose,
  onConfirm,
  deadline,
  mode,
  deadlineLabel,
  isPending,
  isSettingTime,
  isTransitioning,
}: TimeShiftDialogProps) {
  const [hoursOffset, setHoursOffset] = useState(2);

  // Reset offset when dialog opens
  useEffect(() => {
    if (open) {
      setHoursOffset(2);
    }
  }, [open]);

  const targetTime = useMemo(() => {
    if (mode === 'before') {
      return subHours(deadline, hoursOffset);
    }
    return addHours(deadline, hoursOffset);
  }, [deadline, hoursOffset, mode]);

  const handleConfirm = () => {
    onConfirm(targetTime);
  };

  const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 168) {
      // Max 1 week
      setHoursOffset(value);
    }
  };

  const getProgressText = () => {
    if (isSettingTime) return 'Setting time...';
    if (isTransitioning) return 'Transitioning pots...';
    return 'Processing...';
  };

  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Shift Time: {deadlineLabel}</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Deadline
          </Typography>
          <Typography variant="body1" fontWeight={500} gutterBottom>
            {format(deadline, 'MMM d, yyyy \u2022 h:mm a')}
          </Typography>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Set time to</Typography>
            <TextField
              type="number"
              size="small"
              value={hoursOffset}
              onChange={handleHoursChange}
              inputProps={{ min: 0, max: 168 }}
              sx={{ width: 80 }}
              disabled={isPending}
            />
            <Typography variant="body2">
              hour{hoursOffset !== 1 ? 's' : ''}{' '}
              {mode === 'before' ? 'before' : 'after'}
            </Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Target time
            </Typography>
            <Typography variant="body1" fontWeight={600} color="primary">
              {format(targetTime, 'MMM d, yyyy \u2022 h:mm a')}
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mt: 3 }}>
            This will also trigger the pot status transitions job.
          </Alert>

          {isPending && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {getProgressText()}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isPending}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
